import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../../ui/card';
import { Lock } from 'lucide-react';
import type { UserInsight } from '../../../types/insight';

interface ClimberIndexCardProps {
  insight: UserInsight;
}

/**
 * The headline. It has three states, and the two locked ones carry as much
 * design as the unlocked one: on a young platform almost every climber sees a
 * locked card, so it has to read as "not yet" rather than as "broken".
 */
export default function ClimberIndexCard({ insight }: ClimberIndexCardProps) {
  const { t } = useTranslation();

  if (!insight.scored) {
    return <NotEnoughSends insight={insight} />;
  }

  const topPercent = insight.percentile === null ? null : 100 - insight.percentile;

  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{t('userProfile.insights.indexLabel')}</p>

        <div className="mt-1 flex items-baseline justify-between gap-4">
          <span className="text-4xl font-bold tabular-nums">
            {insight.index_score?.toFixed(1) ?? '—'}
          </span>
          {insight.ranked && topPercent !== null && (
            <span className="text-lg font-semibold text-primary">
              {t('userProfile.insights.topPercent', { percent: topPercent })}
            </span>
          )}
        </div>

        {insight.ranked && insight.percentile !== null ? (
          <>
            <div
              className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted"
              role="img"
              aria-label={t('userProfile.insights.standingAria', {
                percent: topPercent,
                count: insight.cohort_size,
              })}
            >
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${insight.percentile}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {t('userProfile.insights.amongClimbers', { count: insight.cohort_size })}
            </p>
          </>
        ) : (
          <RankingLocked insight={insight} />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Scored, but the cohort is too small to rank against. The index and the rare
 * sends stay visible — only the standing is withheld, and we say why.
 */
function RankingLocked({ insight }: { insight: UserInsight }) {
  const { t } = useTranslation();
  const missing = Math.max(insight.thresholds.min_cohort_size - insight.cohort_size, 0);

  return (
    <div className="mt-3 flex items-start gap-2 rounded-md bg-muted/50 p-3">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <p className="text-xs text-muted-foreground">
        {t('userProfile.insights.rankingLocked', {
          count: missing,
          total: insight.thresholds.min_cohort_size,
        })}
      </p>
    </div>
  );
}

/** Not enough sends for an index yet — shown with progress toward the floor. */
function NotEnoughSends({ insight }: { insight: UserInsight }) {
  const { t } = useTranslation();
  const { min_qualifying_sends: floor } = insight.thresholds;
  const done = Math.min(insight.qualifying_sends_count, floor);
  const progress = floor === 0 ? 0 : (done / floor) * 100;

  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-medium">{t('userProfile.insights.notScoredTitle')}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('userProfile.insights.notScoredBody', { count: insight.sends_needed })}
        </p>

        <div
          className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={t('userProfile.insights.progressAria', { done, total: floor })}
        >
          <div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-2 text-xs tabular-nums text-muted-foreground">
          {t('userProfile.insights.progressCount', { done, total: floor })}
        </p>
      </CardContent>
    </Card>
  );
}
